'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface Address {
  id: string;
  label: string;
  full_name: string;
  phone: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string;
  pincode: string;
  is_default: boolean;
}

const EMPTY_FORM = { label: 'Home', full_name: '', phone: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '', is_default: false };

export default function CustomerAddressesPage() {
  const router = useRouter();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState('');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadAddresses = async (uid: string) => {
    const { data } = await supabase.from('customer_addresses').select('*').eq('customer_id', uid).order('is_default', { ascending: false });
    setAddresses(data || []);
    setLoading(false);
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { router.push('/customer-login'); return; }
      setUserId(user.id);
      loadAddresses(user.id);
    });
  }, [router]);

  const handleSave = async () => {
    if (!form.full_name || !form.address_line1 || !form.city || !form.state || !form.pincode) {
      showToast('Please fill in all required fields.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        const { error } = await supabase.from('customer_addresses').update({ ...form, address_line2: form.address_line2 || null }).eq('id', editId);
        if (error) throw error;
        showToast('Address updated!', 'success');
      } else {
        const { error } = await supabase.from('customer_addresses').insert({ ...form, address_line2: form.address_line2 || null, customer_id: userId });
        if (error) throw error;
        showToast('Address added!', 'success');
      }
      setShowForm(false);
      setEditId(null);
      setForm(EMPTY_FORM);
      loadAddresses(userId);
    } catch (err: any) {
      showToast(err.message || 'Failed to save address.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('customer_addresses').delete().eq('id', id);
    if (!error) { showToast('Address deleted.', 'success'); loadAddresses(userId); }
  };

  const handleSetDefault = async (id: string) => {
    await supabase.from('customer_addresses').update({ is_default: false }).eq('customer_id', userId);
    await supabase.from('customer_addresses').update({ is_default: true }).eq('id', id);
    loadAddresses(userId);
  };

  const handleEdit = (addr: Address) => {
    setForm({ label: addr.label, full_name: addr.full_name, phone: addr.phone, address_line1: addr.address_line1, address_line2: addr.address_line2 || '', city: addr.city, state: addr.state, pincode: addr.pincode, is_default: addr.is_default });
    setEditId(addr.id);
    setShowForm(true);
  };

  return (
    <CustomerDashboardLayout>
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-500 shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationTriangleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-700 text-slate-900">My Addresses</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage your delivery addresses</p>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY_FORM); }}
            className="btn-primary px-4 py-2.5 rounded-xl text-sm font-600 flex items-center gap-2"
          >
            <Icon name="PlusIcon" size={16} /> Add Address
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
          </div>
        ) : addresses.length === 0 && !showForm ? (
          <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
            <Icon name="MapPinIcon" size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No addresses saved yet</p>
            <button onClick={() => setShowForm(true)} className="mt-4 btn-primary px-5 py-2 rounded-xl text-sm font-600">Add your first address</button>
          </div>
        ) : (
          <div className="space-y-3">
            {addresses.map(addr => (
              <div key={addr.id} className={`bg-white border rounded-xl p-5 ${addr.is_default ? 'border-primary' : 'border-slate-200'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-700 text-slate-900">{addr.label}</span>
                      {addr.is_default && <span className="text-xs bg-primary/10 text-primary font-600 px-2 py-0.5 rounded-full">Default</span>}
                    </div>
                    <p className="text-sm text-slate-700 font-500">{addr.full_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}</p>
                    <p className="text-xs text-slate-500">{addr.city}, {addr.state} - {addr.pincode}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{addr.phone}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => handleEdit(addr)} className="text-xs text-primary font-600 hover:underline">Edit</button>
                    {!addr.is_default && <button onClick={() => handleSetDefault(addr.id)} className="text-xs text-slate-500 hover:text-primary font-500">Set Default</button>}
                    <button onClick={() => handleDelete(addr.id)} className="text-xs text-red-500 hover:underline font-500">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h2 className="text-base font-700 text-slate-900">{editId ? 'Edit Address' : 'Add New Address'}</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Label</label>
                <select value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                  {['Home', 'Work', 'Other'].map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Full Name *</label>
                <input type="text" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Phone *</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Pincode *</label>
                <input type="text" value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-600 text-slate-500 mb-1.5">Address Line 1 *</label>
              <input type="text" value={form.address_line1} onChange={e => setForm(f => ({ ...f, address_line1: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-slate-500 mb-1.5">Address Line 2</label>
              <input type="text" value={form.address_line2} onChange={e => setForm(f => ({ ...f, address_line2: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">City *</label>
                <input type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">State *</label>
                <input type="text" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} className="rounded" />
              <span className="text-sm text-slate-700">Set as default address</span>
            </label>
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving} className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600 disabled:opacity-60 flex items-center gap-2">
                {saving ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" /> Saving…</> : 'Save Address'}
              </button>
              <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-6 py-2.5 rounded-xl text-sm font-600 border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </CustomerDashboardLayout>
  );
}
